import axios, { AxiosError } from "axios";
import { id } from "date-fns/locale";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import * as React from "react";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import IconButton from "@mui/material/IconButton";
import visuallyHidden from "@mui/utils/visuallyHidden";
import { format } from "date-fns";
import EditIcon from "@mui/icons-material/Edit";
import { Link as RouterLink } from "react-router-dom";
import { Order, stableSort, getComparator } from "../ui/Tables";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { TabellaRisorse } from "./TabellaRisorse";
import { oraPrenotaStore } from "../oraPrenotaStore";
import Collapse from "@mui/material/Collapse";
import TextField from "@mui/material/TextField";
import Link from "@mui/material/Link";
import { uiStore } from "../uiStore";

interface Data {
  id: number;
  codice: string;
  nome: string;
  attivo: number;
  dataAttivazione: string;
  dataScadenza: string;
}

interface HeadCell {
  disablePadding: boolean;
  id: keyof Data;
  label: string;
  numeric: boolean;
  key?: string;
  sortable: boolean;
  filter: boolean;
}

const headCells: readonly HeadCell[] = [
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "",
    sortable: false,
    filter: false,
    key: "id2",
  },
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "Id",
    key: "id3",
    sortable: true,
    filter: false,
  },
  {
    id: "attivo",
    numeric: false,
    disablePadding: false,
    label: "Attivo",
    sortable: true,
    filter: false,
  },
  {
    id: "codice",
    numeric: false,
    disablePadding: false,
    label: "Codice",
    sortable: true,
    filter: true,
  },
  {
    id: "nome",
    numeric: false,
    disablePadding: false,
    label: "Nome",
    sortable: true,
    filter: true,
  },

  {
    id: "dataAttivazione",
    numeric: false,
    disablePadding: false,
    label: "Attivazione",
    sortable: true,
    filter: true,
  },
  {
    id: "dataScadenza",
    numeric: false,
    disablePadding: false,
    label: "Scadenza",
    sortable: true,
    filter: true,
  },
  {
    id: "id",
    numeric: true,
    disablePadding: false,
    label: "",
    key: "id2",
    sortable: false,
    filter: false,
  },
];

interface EnhancedTableProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: keyof Data) => void;
  order: Order;
  orderBy: string;
  rowCount: number;
  onFilter: (event: any, property: keyof Data, filter: string | number) => void;
}

function EnhancedTableHead(props: EnhancedTableProps) {
  const { order, orderBy, onRequestSort, onFilter } = props;
  const createSortHandler = (property: keyof Data) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property);
  };
  const createFilterHandler = (property: keyof Data) => (event: any) => {
    onFilter(event, property, event.target.value);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.key ? headCell.key : headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            {headCell.sortable && (
              <TableSortLabel
                active={orderBy === headCell.id}
                direction={orderBy === headCell.id ? order : "asc"}
                onClick={createSortHandler(headCell.id)}
              >
                {headCell.label}
                {orderBy === headCell.id ? (
                  <Box component="span" sx={visuallyHidden}>
                    {order === "desc" ? "sorted descending" : "sorted ascending"}
                  </Box>
                ) : null}
              </TableSortLabel>
            )}
          </TableCell>
        ))}
      </TableRow>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.key ? headCell.key : headCell.id}>
            {headCell.filter && (
              <TextField label="" variant="standard" onChange={createFilterHandler(headCell.id)} />
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

function Row(props: { row: Data }) {
  const { row } = props;
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover tabIndex={-1}>
        <TableCell sx={{ whiteSpace: "nowrap" }}>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            sx={{ mr: 2 }}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>

          <Link to={"/app/editClub/" + row.id} underline="none" component={RouterLink}>
            <IconButton aria-label="edit" sx={{ p: 0 }}>
              <EditIcon />
            </IconButton>
          </Link>
        </TableCell>
        <TableCell>{row.id}</TableCell>
        <TableCell>{row.attivo ? "Si" : "No"}</TableCell>
        <TableCell>{row.codice}</TableCell>
        <TableCell>{row.nome}</TableCell>
        <TableCell>{format(new Date(row.dataAttivazione), "dd/MM/yyyy")}</TableCell>
        <TableCell>{format(new Date(row.dataScadenza), "dd/MM/yyyy")}</TableCell>
        <TableCell>
          <Link to={"/app/createRisorsa/" + row.id} underline="none" component={RouterLink}>
            <IconButton aria-label="aggiungi campo" sx={{ p: 0 }}>
              <AddCircleOutlineIcon />
            </IconButton>
          </Link>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, paddingLeft: "50px" }} colSpan={7}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <TabellaRisorse clubId={row.id} />
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export function Clubs() {
  const { isLoading, data } = useQuery<unknown, AxiosError, { clubs: Data[] }>(
    ["getClubs", id],
    () => axios.get(API_URL + "club/clubs").then((res) => res.data)
  );

  const dense = oraPrenotaStore((state) => state.tabelleCompatte);

  const rows = data?.clubs ?? [];

  const [order, setOrder] = React.useState<Order>("asc");
  const [orderBy, setOrderBy] = React.useState<keyof Data>("id");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [filters, setFilters] = React.useState({});

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: keyof Data) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleFilterRequest = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
    filter: string | number
  ) => {
    setFilters({
      ...filters,
      [property]: filter,
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

  const setTitle = uiStore((state) => state.setTitle);
  useEffect(() => {
    setTitle("Clubs");
  }, [setTitle]);

  if (isLoading) return <></>;

  return (
    <Box sx={{ width: "100%" }}>
      {/* <Title title={"Clubs"} /> */}
      <TableContainer>
        <Table size={dense ? "small" : "medium"}>
          <EnhancedTableHead
            order={order}
            orderBy={orderBy}
            onRequestSort={handleRequestSort}
            rowCount={rows.length}
            onFilter={handleFilterRequest}
          />
          <TableBody classes={{ root: "table-body" }}>
            {stableSort(rows, getComparator(order, orderBy), filters)
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => {
                return <Row row={row} key={row.id} />;
              })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: (dense ? 33 : 53) * emptyRows,
                }}
              ></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
        component="div"
        count={rows.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Box>
  );
}
